import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, useEffect, useState } from 'react';
import { Col, Container } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import RetireImg from '../../assets/retire-online.svg';
import Button from '../../components/Button';
import DefaultCard from '../../components/DefaultCard';
import GenericCards from '../../components/GenericCards';
import RetireOnlineRequiredActions from '../../components/RetireOnlineRequiredActions';
import CustomSelect from '../../components/Select';
import useModal from '../../hooks/useModal';
import PensionElectionChecklistModal from '../widgets/PensionElectionChecklist';
import PensionEstimateModal from '../widgets/pensionEstimate/pensionEstimateModal';
import RetireOnlineSuccessScreen from '../widgets/retireOnlineModals/RetireOnlineSuccessScreen';
import RetireOnlineStepper from '../widgets/retireOnlineStepper';

const RetireOnline = () => {
  const { pathname } = useLocation();
  const [
    showPensionEstimateModal,
    handleShowPensionModal,
    handleClosePensionModal,
  ] = useModal();

  const [isStepperVisible, setStepperVisible] = useState(false);
  const handleStepperVisible = () => {
    setStepperVisible(true);
  };

  const [
    showPensionElectionChecklistModal,
    handleShowPensionElectionChecklistModal,
    handleHidePensionElectionChecklistModal,
  ] = useModal();

  // To test the "Actions Required" - change to: useState(true)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [requiredActionsCompleted, setRequiredActionsCompleted] =
    useState(false);

  const [successScreen, setSuccessScreen] = useState(false);

  const [startScreen, setStartScreen] = useState(true);

  const handleRetirementCompletion = (isVisible: boolean) => {
    setStepperVisible(isVisible);

    if (isVisible) {
      sessionStorage.setItem('isActionsRequired', '1');
    }
  };

  useEffect(() => {
    if(pathname.indexOf('start') > -1) {
      handleOnStateChange('1');
    }
    if(pathname.indexOf('required') > -1) {
      handleOnStateChange('2');
    }
    if(pathname.indexOf('success') > -1) {
      handleOnStateChange('3');
    }
  }, [pathname]);

  const options = [
    { value: '1', label: 'Start' },
    { value: '2', label: 'Actions Required' },
    { value: '3', label: 'Success' },
  ];

  const handleOnStateChange = (e: any) => {
    if (e === '1') {
      setStepperVisible(false);
      sessionStorage.removeItem('isActionsRequired');
      setStartScreen(true);
      setRequiredActionsCompleted(false);
      setSuccessScreen(false);
    } else if (e === '2') {
      setStepperVisible(false);
      sessionStorage.setItem('isActionsRequired', '1');
      setStartScreen(false);
      setRequiredActionsCompleted(true);
      setSuccessScreen(false);
    } else if (e === '3') {
      setStepperVisible(false);
      sessionStorage.removeItem('isActionsRequired');
      setStartScreen(false);
      setRequiredActionsCompleted(false);
      setSuccessScreen(true);
    }
  };

  useEffect(() => {
    const isActionsRequired = sessionStorage.getItem('isActionsRequired');
    if (isActionsRequired === '1') {
      setRequiredActionsCompleted(true);
      setStartScreen(false);
    }
  }, []);

  return (
    <>
      <Container className="cnd-custom-container my-12 px-lg-9 px-xl-0">
        {!isStepperVisible ? (
          <Fragment>
            <section className="d-lg-flex flex-lg-row justify-content-between">
              <Col className="col-12 col-lg-6 d-flex flex-column justify-content-center">
                <h3 className="lh-xs text-sm-20-lg-48">Retire Online</h3>
                <span className="text-fs-6 lh-xs fw-light">
                  Deciding when to collect pension plan benefits involves some
                  of the most important decisions of your life. You have a
                  variety of options for your benefit plans. Make sure to review
                  the Pension Election Checklist, to gather everything you will
                  need to complete this event.
                </span>
              </Col>
              <Col className="pt-12 pt-lg-0 col-12 col-lg-6 d-lg-flex justify-content-lg-end">
                <img src={RetireImg} alt="retire-online" />
              </Col>
            </section>

            {startScreen && (
              <>
                <section className="d-lg-flex flex-lg-row pt-10">
                  <Col className="col-12 col-lg-4">
                    <Container className="pe-lg-3 px-0">
                      <GenericCards
                        variant="transperant"
                        className="d-flex flex-column border p-10 rounded-1"
                      >
                        <h5 className="lh-sm mb-0">
                          Pension Election Checklist
                        </h5>
                        <span className="fw-light text-fs-6 lh-xs">
                          Review the Pension Election Checklist so that you can
                          easily complete the online application.
                        </span>
                        <span>
                          <Button
                            onMouseDown={
                              handleShowPensionElectionChecklistModal
                            }
                            variant="primary-outline"
                            className="mt-9"
                            rightIcon={light('list-check')}
                          >
                            Review Checklist
                          </Button>
                        </span>
                      </GenericCards>
                    </Container>
                  </Col>
                  <Col className="col-12 col-lg-4">
                    <Container className="py-7 py-lg-0 px-0 ps-lg-3">
                      <GenericCards
                        variant="transperant"
                        className="d-flex flex-column border p-10 rounded-1"
                      >
                        <h5 className="lh-sm mb-0">Estimate Pension Payment</h5>
                        <span className="fw-light text-fs-6 lh-xs">
                          Determine your beneficiary, retirement date and the
                          payment form.
                        </span>
                        <span>
                          <Button
                            variant="primary-outline"
                            className="mt-9"
                            onClick={handleShowPensionModal}
                          >
                            Checkout the Model
                          </Button>
                          <PensionEstimateModal
                            showModal={showPensionEstimateModal}
                            handleClose={handleClosePensionModal}
                          />
                        </span>
                      </GenericCards>
                    </Container>
                  </Col>
                  <Col className="col-12 col-lg-4">
                    <Container className="ps-lg-7 px-0">
                      <GenericCards
                        variant="transperant"
                        className="d-flex flex-column p-10"
                      >
                        <h5 className="text-nowrap">Things you’ll need</h5>
                        <div className="fw-normal text-fs-6 lh-xs pt-3 d-flex align-items-center">
                          <FontAwesomeIcon
                            icon={light('check')}
                            className="pe-3 fill-marks--green"
                          />
                          <span className="text-indigo">
                            Marriage Certificate
                          </span>
                        </div>
                        <div className="fw-normal text-fs-6 lh-xs pt-3 d-flex align-items-center">
                          <FontAwesomeIcon
                            icon={light('check')}
                            className="pe-3 fill-marks--green"
                          />
                          <span className="text-indigo">Birth Certificate</span>
                        </div>
                        <div className="fw-normal text-fs-6 lh-xs pt-3 d-flex align-items-center">
                          <FontAwesomeIcon
                            icon={light('check')}
                            className="pe-3 fill-marks--green"
                          />
                          <span className="text-indigo">
                            Your Beneficiary’s Birth Certificate
                          </span>
                        </div>
                        <div className="fw-normal text-fs-6 lh-xs pt-3 d-flex align-items-center">
                          <FontAwesomeIcon
                            icon={light('check')}
                            className="pe-3 fill-marks--green"
                          />
                          <span className="text-indigo">Divorce Decree</span>
                        </div>
                      </GenericCards>
                    </Container>
                  </Col>
                </section>
                <section className="pt-10">
                  <DefaultCard
                    variant="blue"
                    leftHeading="Ready to go?"
                    leftSubheading="Once you have completed the Checklist and used the Estimate Tool you are ready to get started."
                    button={
                      <Button
                        variant="primary-shadow"
                        size="sm"
                        rightIcon={light('arrow-right')}
                        onClick={handleStepperVisible}
                      >
                        Get Started
                      </Button>
                    }
                  />
                </section>
              </>
            )}
            {requiredActionsCompleted && <RetireOnlineRequiredActions />}
            {successScreen && <RetireOnlineSuccessScreen />}
            <section className="pt-10 d-flex flex-column flex-lg-row align-items-lg-center">
              <Col className="p-0 col-lg-5">
                <div className="fw-light text-sub1">
                  <span>
                    Switch between different stages of the retirement process
                  </span>
                </div>
              </Col>
              <Col className="p-0 pt-5 pt-lg-0 col-lg-3">
                <div>
                  <CustomSelect
                    options={options}
                    onChange={handleOnStateChange}
                  />
                </div>
              </Col>
            </section>
          </Fragment>
        ) : (
          <RetireOnlineStepper isVisible={handleRetirementCompletion} />
        )}
      </Container>
      <PensionElectionChecklistModal
        showPensionElectionChecklistModal={showPensionElectionChecklistModal}
        handleHidePensionElectionChecklistModal={
          handleHidePensionElectionChecklistModal
        }
      />
    </>
  );
};

export default RetireOnline;
